import srraf from "srraf";

import { emit, on } from "@/glow/events";

const selectors = {
  headerContainer: ".header-container",
  headerWrapper: "#header",
  announcementBar: ".announcement-bar-section",
  logoWrapper: ".header__logo-wrapper",
};

export default function StickyHeader(node) {
  const root = document.documentElement;
  // Elements can change when resizing or altering header
  let headerParent = null;
  let headerHeight = null;
  let headerWrapper = null;
  let announcementBar = null;
  let announcementBarHeight = null;
  let logo = null;
  let headerHasCustomLogoImage = null;

  let transparentEnabled = null;

  let scroller = null;
  let stickyScroller = null;

  // Breakpoint is equal to 60em
  const mediumBP = 960;
  const offsetRootVar = "--header-offset-height";

  // get elements & element heights
  const _defineElements = () => {
    headerParent = document.querySelector(selectors.headerContainer);
    headerWrapper = node.querySelector(selectors.headerWrapper);
    announcementBar = node.querySelector(selectors.announcementBar);
    announcementBarHeight = announcementBar ? announcementBar.offsetHeight : 0;
    headerHeight = announcementBarHeight + headerWrapper.offsetHeight;
    logo = node.querySelector(selectors.logoWrapper);
    headerHasCustomLogoImage = node.querySelector(".header__logo-image img");
    // True if the transparent header is enabled in the theme editor
    transparentEnabled = JSON.parse(headerWrapper.dataset.transparentHeader);
  };

  _defineElements();

  const _screenUnderMediumBP = () => {
    const viewportWidth =
      window.innerWidth || document.documentElement.clientWidth;

    return viewportWidth <= mediumBP;
  };

  let screenUnderMediumBP = _screenUnderMediumBP();

  scroller = srraf(({ vw }) => {
    _setRootVar(
      "--header-height",
      announcementBarHeight + headerWrapper.offsetHeight
    );

    const currentScreenWidthUnderMediumBP = vw <= mediumBP;

    if (currentScreenWidthUnderMediumBP !== screenUnderMediumBP) {
      screenUnderMediumBP = currentScreenWidthUnderMediumBP;
      _setupStickyHeader();
    }
  });

  const _setRootVar = (name, value) => {
    root.style.setProperty(name, `${value}px`);
  };

  const _setHeaderType = () => {
    // Remove header types incase of customizer changes
    root.classList.remove("header-transparent", "header-sticky");

    root.classList.add(
      transparentEnabled ? "header-transparent" : "header-sticky"
    );
  };

  const _setupStickyHeader = () => {
    //redifine elements & heights
    _defineElements();

    // If the header has a logo image we calculate values after image has loaded
    if (
      !headerHasCustomLogoImage ||
      (headerHasCustomLogoImage && headerHasCustomLogoImage.complete)
    ) {
      _processStickyHeader();
    } else {
      headerHasCustomLogoImage.addEventListener("load", _processStickyHeader, {
        once: true,
      });
    }
  };

  const _processStickyHeader = () => {
    _defineElements();
    _setRootVar(offsetRootVar, headerHeight);
    _setHeaderType();
    _initStickyHeader(_getStickyOffsets());
  };

  // Returns object of offset values
  const _getStickyOffsets = () => {
    // Get the headers base padding-top string value
    const headerBaseTopPaddingPixelValue =
      getComputedStyle(headerWrapper).getPropertyValue("padding-top");

    // Get the int value of the header base padding
    const headerBaseTopPadding = parseInt(headerBaseTopPaddingPixelValue);

    const headerDesktopStyleIsMobile = headerWrapper.classList.contains(
      "header--always-mobile"
    );
    const headerLogoHeight = logo.offsetHeight;

    const offsets = {};

    offsets.stickyHeaderResetPosition = 0;

    // Set offSets based on size and content of header
    if (
      (screenUnderMediumBP && headerHasCustomLogoImage) ||
      (headerDesktopStyleIsMobile && headerHasCustomLogoImage)
    ) {
      const headerTopBottomPadding = headerBaseTopPadding * 2;
      offsets.offsetHeight =
        headerLogoHeight - headerTopBottomPadding + announcementBarHeight;
      offsets.scrollYToSticky =
        headerLogoHeight - headerTopBottomPadding + announcementBarHeight;
    } else if (screenUnderMediumBP) {
      if (announcementBarHeight) {
        offsets.scrollYToSticky = announcementBarHeight;
      } else {
        // Need to default to 1 to not enable sticky header at all times on mobile
        offsets.scrollYToSticky = 1;
        offsets.stickyHeaderResetPosition = 1;
      }

      offsets.offsetHeight = announcementBarHeight;
    } else if (headerDesktopStyleIsMobile) {
      offsets.offsetHeight = announcementBarHeight ? announcementBarHeight : 1;
      offsets.scrollYToSticky = announcementBarHeight
        ? announcementBarHeight
        : 1;
    } else {
      offsets.offsetHeight =
        headerLogoHeight + announcementBarHeight + headerBaseTopPadding;
      offsets.scrollYToSticky =
        announcementBarHeight + headerLogoHeight + headerBaseTopPadding;
    }

    return offsets;
  };

  const _initStickyHeader = offsets => {
    const { scrollYToSticky, offsetHeight, stickyHeaderResetPosition } =
      offsets;

    // Destroy stickyScroller if one already exists
    if (stickyScroller) {
      stickyScroller.destroy();
    }

    // Init the scroller to monitor for y position to stick/unstick header
    stickyScroller = srraf(({ y }) => {
      if (y < scrollYToSticky) {
        _toggleStickyHeader(false, headerParent);
        _setElementTopPosition(headerParent, stickyHeaderResetPosition);
        _setRootVar(offsetRootVar, headerHeight - y);
      } else if (y >= scrollYToSticky) {
        _toggleStickyHeader(true, headerParent);
        _setElementTopPosition(headerParent, -offsetHeight);
        _setRootVar(offsetRootVar, headerHeight - offsetHeight);
      }
    });

    stickyScroller.update();
  };

  const _setElementTopPosition = (element, value) => {
    element.style.top = `${value}px`;
  };

  const _toggleStickyHeader = (isSticky, element) => {
    if (isSticky) {
      element.classList.add("is-sticky");
      headerWrapper.classList.remove("header--transparent");
    } else {
      element.classList.remove("is-sticky");

      if (transparentEnabled) {
        headerWrapper.classList.add("header--transparent");
      }
    }
  };

  const _reload = () => {
    scroller.update();
    _setupStickyHeader();

    // Let pages know the header changed
    emit("headerChange", () => {});
  };

  on("sticky-header:reload", () => {
    _reload();
  });

  _setupStickyHeader();

  const unload = () => {
    if (scroller) {
      scroller.destroy();
    }
    if (stickyScroller) {
      stickyScroller.destroy();
    }
  };

  return { unload };
}
